import React, { useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip
} from '@mui/material';
import { OpenInFull, ContentCopy } from '@mui/icons-material';

/**
 * Read-only, scrollable release notes.
 * - Keeps line breaks and bullets the uploader typed (white-space: pre-wrap)
 * - Fixed max height with its own scrollbar, so long notes don't stretch the table row
 * - Optional "expand" to read the notes in a full dialog
 *
 * Usage in the builds table:
 *   <ReleaseNotesViewer notes={build.release_notes} title={`v${build.version_label}`} expandable />
 */
export const ReleaseNotesViewer = ({ notes, maxHeight = 120, expandable = false, title = 'Release Notes' }) => {
  const [open, setOpen] = useState(false);
  const text = (notes || '').trim();

  if (!text) {
    return <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>No release notes</Typography>;
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Box
          tabIndex={0} // keyboard scrollable
          sx={{
            maxHeight,
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.85rem',
            lineHeight: 1.6,
            color: '#334155',
            bgcolor: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            p: 1.5,
            pr: expandable ? 5 : 1.5,
            '&:focus-visible': { outline: '2px solid #2F727C' },
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 },
          }}
        >
          {text}
        </Box>
        {expandable && (
          <Tooltip title="Expand">
            <IconButton size="small" onClick={() => setOpen(true)}
              sx={{ position: 'absolute', top: 4, right: 4, bgcolor: '#f8fafc' }}>
              <OpenInFull fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {expandable && (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 800, color: '#1A3A3F' }}>{title}</DialogTitle>
          <DialogContent dividers>
            <Typography component="div" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: '#334155' }}>
              {text}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button startIcon={<ContentCopy />} onClick={() => navigator.clipboard?.writeText(text)}>Copy</Button>
            <Button variant="contained" onClick={() => setOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};